/**
 * ========================
 * DOMAIN MENU REGISTRY
 * ========================
 *
 * This is the central in-memory registry of all domain menu providers.
 *
 * Domain providers register themselves at app startup.
 * The MenuRenderer then queries this registry based on active domain.
 *
 * This pattern prevents:
 *  - Menu engine importing domain modules
 *  - Circular dependencies
 *  - Tight coupling between UI and domain logic
 *
 * ARCHITECTURE:
 *  UI Components → Registry → Providers
 *       ❌ ← ← ← ← ← ← ← ← ← ←
 *
 * Registry is the ONLY bridge between UI and domain logic.
 */

import { DomainMenuProvider, GlobalMenuProvider } from '../layouts/menu/types';

/**
 * ========================
 * REGISTRY STORAGE
 * ========================
 */

/** Domain-specific menu providers (keyed by domainId) */
const domainProviders = new Map<string, DomainMenuProvider>();

/** Global menu providers (typically just one: 'global') */
const globalProviders = new Map<string, GlobalMenuProvider>();

/** Development mode flag for warnings */
const isDev = import.meta.env.DEV;

/**
 * ========================
 * DOMAIN PROVIDER REGISTRATION
 * ========================
 */

/**
 * Register a domain menu provider.
 *
 * MUST be called exactly once per provider at app startup.
 * Typically called via side-effect import in main.tsx.
 *
 * If a provider with the same domainId already exists:
 * - In DEV: Warns and replaces (supports HMR)
 * - In PROD: Silently replaces (intentional override)
 *
 * @param provider - The domain menu provider to register
 *
 * @example
 * ```typescript
 * import { registerMenuProvider } from '../domain/DomainMenuRegistry';
 * import { tasksMenuProvider } from './providers/tasksMenu';
 *
 * registerMenuProvider(tasksMenuProvider);
 * ```
 */
export function registerMenuProvider(provider: DomainMenuProvider): void {
  const { domainId } = provider;

  if (isDev && domainProviders.has(domainId)) {
    console.warn(`[MenuRegistry] Replacing domain provider: ${domainId}`);
  }

  domainProviders.set(domainId, provider);

  // Call optional initialize hook
  if (provider.initialize) {
    try {
      provider.initialize();
    } catch (error) {
      console.error(`[MenuRegistry] Failed to initialize provider: ${domainId}`, error);
    }
  }

  if (isDev) {
    console.log(`[MenuRegistry] Registered domain provider: ${domainId}`);
  }
}

/**
 * Register a global menu provider.
 *
 * Global providers handle app-wide navigation (App/User/Admin sections).
 * Typically only one global provider exists with id='global'.
 *
 * @param provider - The global menu provider to register
 *
 * @example
 * ```typescript
 * import { registerGlobalMenuProvider } from '../domain/DomainMenuRegistry';
 * import { globalMenuProvider } from './providers/globalMenu';
 *
 * registerGlobalMenuProvider(globalMenuProvider);
 * ```
 */
export function registerGlobalMenuProvider(provider: GlobalMenuProvider): void {
  const { id } = provider;

  if (isDev && globalProviders.has(id)) {
    console.warn(`[MenuRegistry] Replacing global provider: ${id}`);
  }

  globalProviders.set(id, provider);

  // Call optional initialize hook
  if (provider.initialize) {
    try {
      provider.initialize();
    } catch (error) {
      console.error(`[MenuRegistry] Failed to initialize global provider: ${id}`, error);
    }
  }

  if (isDev) {
    console.log(`[MenuRegistry] Registered global provider: ${id}`);
  }
}

/**
 * ========================
 * PROVIDER LOOKUP
 * ========================
 */

/**
 * Get a domain menu provider by domain ID.
 *
 * Returns null if domain is unknown.
 * MenuRenderer handles null gracefully by showing fallback UI.
 *
 * @param domainId - The domain identifier
 * @returns The provider or null if not found
 *
 * @example
 * ```typescript
 * const provider = getMenuProvider('tasks');
 * if (provider) {
 *   const config = provider.getMenuConfig();
 *   // render menu from config
 * }
 * ```
 */
export function getMenuProvider(domainId: string): DomainMenuProvider | null {
  return domainProviders.get(domainId) || null;
}

/**
 * Get a global menu provider by ID.
 *
 * Returns null if provider is unknown.
 *
 * @param id - The global provider identifier (typically 'global')
 * @returns The provider or null if not found
 *
 * @example
 * ```typescript
 * const provider = getGlobalMenuProvider('global');
 * if (provider) {
 *   const config = provider.getMenuConfig();
 *   // render global menu from config
 * }
 * ```
 */
export function getGlobalMenuProvider(id: string): GlobalMenuProvider | null {
  return globalProviders.get(id) || null;
}

/**
 * ========================
 * DEBUGGING HELPERS
 * ========================
 */

/**
 * Get list of all registered domain IDs.
 *
 * Useful for debugging and validation.
 * Only available in development builds.
 *
 * @returns Array of registered domain IDs
 */
export function getRegisteredDomains(): string[] {
  return Array.from(domainProviders.keys());
}

/**
 * Get list of all registered global provider IDs.
 *
 * Useful for debugging and validation.
 * Only available in development builds.
 *
 * @returns Array of registered global provider IDs
 */
export function getRegisteredGlobalProviders(): string[] {
  return Array.from(globalProviders.keys());
}

/**
 * Unregister a domain menu provider.
 *
 * Calls optional destroy() hook before removal.
 * Rarely used — typically only in tests or dynamic plugin systems.
 *
 * @param domainId - The domain identifier to unregister
 * @returns true if provider was found and removed, false otherwise
 */
export function unregisterMenuProvider(domainId: string): boolean {
  const provider = domainProviders.get(domainId);

  if (!provider) {
    return false;
  }

  // Call optional destroy hook
  if (provider.destroy) {
    try {
      provider.destroy();
    } catch (error) {
      console.error(`[MenuRegistry] Failed to destroy provider: ${domainId}`, error);
    }
  }

  domainProviders.delete(domainId);

  if (isDev) {
    console.log(`[MenuRegistry] Unregistered domain provider: ${domainId}`);
  }

  return true;
}

/**
 * Unregister a global menu provider.
 *
 * Calls optional destroy() hook before removal.
 * Rarely used — typically only in tests.
 *
 * @param id - The global provider identifier to unregister
 * @returns true if provider was found and removed, false otherwise
 */
export function unregisterGlobalMenuProvider(id: string): boolean {
  const provider = globalProviders.get(id);

  if (!provider) {
    return false;
  }

  // Call optional destroy hook
  if (provider.destroy) {
    try {
      provider.destroy();
    } catch (error) {
      console.error(`[MenuRegistry] Failed to destroy global provider: ${id}`, error);
    }
  }

  globalProviders.delete(id);

  if (isDev) {
    console.log(`[MenuRegistry] Unregistered global provider: ${id}`);
  }

  return true;
}

/**
 * Clear all registered providers.
 *
 * Useful for testing or full application reset.
 * Calls destroy() on all providers.
 */
export function clearAllProviders(): void {
  // Destroy all domain providers
  domainProviders.forEach((provider, domainId) => {
    if (provider.destroy) {
      try {
        provider.destroy();
      } catch (error) {
        console.error(`[MenuRegistry] Failed to destroy provider: ${domainId}`, error);
      }
    }
  });

  // Destroy all global providers
  globalProviders.forEach((provider, id) => {
    if (provider.destroy) {
      try {
        provider.destroy();
      } catch (error) {
        console.error(`[MenuRegistry] Failed to destroy global provider: ${id}`, error);
      }
    }
  });

  domainProviders.clear();
  globalProviders.clear();

  if (isDev) {
    console.log('[MenuRegistry] Cleared all providers');
  }
}

/**
 * ========================
 * TYPE GUARDS
 * ========================
 */

/**
 * Check if a domain provider is registered.
 *
 * @param domainId - The domain identifier to check
 * @returns true if provider exists, false otherwise
 */
export function hasDomainProvider(domainId: string): boolean {
  return domainProviders.has(domainId);
}

/**
 * Check if a global provider is registered.
 *
 * @param id - The global provider identifier to check
 * @returns true if provider exists, false otherwise
 */
export function hasGlobalProvider(id: string): boolean {
  return globalProviders.has(id);
}
